import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { FormRow } from '../../components/ui/FormRow';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { fmt } from '../../lib/formatters';
import { toast, confirmar } from '../../lib/toast';

const CLIENTE = 'GAME';

type Prov = {
  id: string;
  cliente_id: string;
  fecha: string;
  importe_eur: number;
  descripcion: string;
  observaciones: string | null;
  estado: 'ACTIVO' | 'CERRADO';
  cerrado_at: string | null;
  importe_provisionado_eur: number;
  importe_pendiente_eur: number;
};

type ProvMov = {
  id: string;
  fecha: string;
  numero_documento: string | null;
  importe_eur: number;
  notas: string | null;
  producto_id: string | null;
  sku: string | null;
  producto_nombre: string | null;
};

type BolsaSaldo = { cliente_codigo: string; cliente_nombre: string; saldo_eur: number; movimientos_count: number };

type BolsaMov = {
  id: string;
  fecha: string;
  importe_eur: number;
  descripcion: string;
  observaciones: string | null;
  numero_documento: string | null;
  provisionamiento_origen_id: string | null;
  provisionamiento_origen_desc: string | null;
  cotizacion_id: string | null;
  producto_id: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
};

export function GameBolsaProvisionamiento() {
  const qc = useQueryClient();

  const provs = useQuery({
    queryKey: ['provisionamientos', CLIENTE],
    queryFn: () => api.get<{ data: Prov[] }>(`/provisionamientos?cliente_codigo=${CLIENTE}`),
  });
  const saldo = useQuery({
    queryKey: ['bolsa-saldo', CLIENTE],
    queryFn: () => api.get<{ data: BolsaSaldo[] }>(`/bolsa/saldo?cliente_codigo=${CLIENTE}`),
  });
  const bolsaMov = useQuery({
    queryKey: ['bolsa-mov', CLIENTE],
    queryFn: () => api.get<{ data: BolsaMov[] }>(`/bolsa/movimientos?cliente_codigo=${CLIENTE}`),
  });

  // ───────── Provisionamientos ─────────

  const [openProvForm, setOpenProvForm] = useState(false);
  const [provDraft, setProvDraft] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    importe_eur: '',
    descripcion: '',
    observaciones: '',
  });

  const createProv = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/provisionamientos', { ...body, cliente_codigo: CLIENTE }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provisionamientos', CLIENTE] });
      setOpenProvForm(false);
      setProvDraft({ fecha: new Date().toISOString().slice(0, 10), importe_eur: '', descripcion: '', observaciones: '' });
    },
    onError: (err) => toast.error('No se pudo guardar el provisionamiento', (err as Error).message),
  });

  const deleteProv = useMutation({
    mutationFn: (id: string) => api.delete(`/provisionamientos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provisionamientos', CLIENTE] }),
  });

  const submitProv = () => {
    if (!provDraft.descripcion.trim() || !provDraft.importe_eur) return;
    createProv.mutate({
      fecha: provDraft.fecha,
      importe_eur: Number(provDraft.importe_eur),
      descripcion: provDraft.descripcion.trim(),
      observaciones: provDraft.observaciones || null,
    });
  };

  // Detalle de movimientos de un provisionamiento (modal)
  const [detalleProvId, setDetalleProvId] = useState<string | null>(null);
  const detalleMov = useQuery({
    queryKey: ['prov-mov', detalleProvId],
    queryFn: () => api.get<{ data: ProvMov[] }>(`/provisionamientos/${detalleProvId}/movimientos`),
    enabled: !!detalleProvId,
  });

  // ───────── Bolsa: añadir movimiento manual ─────────

  const [openBolsaForm, setOpenBolsaForm] = useState(false);
  const [bolsaDraft, setBolsaDraft] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    importe_eur: '',
    descripcion: '',
    observaciones: '',
  });

  const createBolsa = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/bolsa/movimientos', { ...body, cliente_codigo: CLIENTE }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bolsa-mov', CLIENTE] });
      qc.invalidateQueries({ queryKey: ['bolsa-saldo', CLIENTE] });
      setOpenBolsaForm(false);
      setBolsaDraft({ fecha: new Date().toISOString().slice(0, 10), importe_eur: '', descripcion: '', observaciones: '' });
    },
  });

  const submitBolsa = () => {
    if (!bolsaDraft.descripcion.trim() || !bolsaDraft.importe_eur) return;
    createBolsa.mutate({
      fecha: bolsaDraft.fecha,
      importe_eur: Number(bolsaDraft.importe_eur),
      descripcion: bolsaDraft.descripcion.trim(),
      observaciones: bolsaDraft.observaciones || null,
    });
  };

  const provsActivos = (provs.data?.data ?? []).filter((p) => p.estado === 'ACTIVO');
  const provsCerrados = (provs.data?.data ?? []).filter((p) => p.estado === 'CERRADO');
  const saldoActual = saldo.data?.data?.[0]?.saldo_eur ?? 0;

  return (
    <div>
      <PageHeader
        title="Bolsa / Provisionamiento — GAME"
        subtitle="Provisionamientos (importes adelantados que recuperamos) y bolsa (saldo a aplicar en cotizaciones)."
      />

      <Card
        title="Bolsa"
        description="Saldo disponible para reducir el coste unitario en cotizaciones. Entradas vienen del excedente de provisionamientos cerrados o aportes manuales."
        actions={<Button size="sm" variant="create" onClick={() => setOpenBolsaForm(true)}>+ Movimiento manual</Button>}
      >
        <div className="mb-4 flex items-center gap-6">
          <div>
            <p className="text-xs uppercase text-slate-500">Saldo actual</p>
            <p className={`text-3xl font-bold ${saldoActual >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt.eur(saldoActual)}</p>
          </div>
          <div className="text-xs text-slate-500">{saldo.data?.data?.[0]?.movimientos_count ?? 0} movimiento(s)</div>
        </div>
        {bolsaMov.isLoading ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Fecha</Th>
                  <Th>Descripción</Th>
                  <Th>Origen</Th>
                  <Th align="right">Importe</Th>
                  <Th>Nota</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(bolsaMov.data?.data ?? []).map((m) => (
                  <tr key={m.id}>
                    <Td>{fmt.date(m.fecha)}</Td>
                    <Td>{m.descripcion}</Td>
                    <Td className="text-xs text-slate-500">
                      {m.provisionamiento_origen_desc ? `Excedente: ${m.provisionamiento_origen_desc}` :
                       m.cotizacion_id ? `Uso en cotización` :
                       'Manual'}
                    </Td>
                    <Td align="right" className={Number(m.importe_eur) >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
                      {fmt.eur(m.importe_eur)}
                    </Td>
                    <Td className="text-xs text-slate-500">{m.observaciones ?? '—'}</Td>
                  </tr>
                ))}
                {(bolsaMov.data?.data ?? []).length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-xs italic text-slate-400">Sin movimientos todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="my-6" />

      <Card
        title={`Provisionamientos activos (${provsActivos.length})`}
        actions={<Button size="sm" variant="create" onClick={() => setOpenProvForm(true)}>+ Nuevo provisionamiento</Button>}
      >
        <ProvList rows={provsActivos} loading={provs.isLoading} onClickDetail={setDetalleProvId} onDelete={async (id) => { if (await confirmar({ title: '¿Eliminar este provisionamiento?', confirmLabel: 'Eliminar', danger: true })) deleteProv.mutate(id); }} />
      </Card>

      {provsCerrados.length > 0 && (
        <div className="mt-6">
          <Card title={`Provisionamientos cerrados (${provsCerrados.length})`}>
            <ProvList rows={provsCerrados} loading={false} onClickDetail={setDetalleProvId} onDelete={async (id) => { if (await confirmar({ title: '¿Eliminar este provisionamiento?', confirmLabel: 'Eliminar', danger: true })) deleteProv.mutate(id); }} />
          </Card>
        </div>
      )}

      {/* Modal: nuevo provisionamiento */}
      <Modal
        open={openProvForm}
        onClose={() => setOpenProvForm(false)}
        title="Nuevo provisionamiento"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenProvForm(false)}>Cancelar</Button>
            <Button
              variant="create"
              onClick={submitProv}
              disabled={!provDraft.descripcion.trim() || !provDraft.importe_eur || createProv.isPending}
            >
              {createProv.isPending ? 'Creando…' : 'Crear'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormRow label="Fecha" required>
            <Input type="date" value={provDraft.fecha} onChange={(e) => setProvDraft((d) => ({ ...d, fecha: e.target.value }))} />
          </FormRow>
          <FormRow label="Importe (EUR)" required>
            <Input type="number" step="0.01" value={provDraft.importe_eur} onChange={(e) => setProvDraft((d) => ({ ...d, importe_eur: e.target.value }))} />
          </FormRow>
          <FormRow label="Descripción (máx 30)" required hint={`${provDraft.descripcion.length}/30`}>
            <Input maxLength={30} value={provDraft.descripcion} onChange={(e) => setProvDraft((d) => ({ ...d, descripcion: e.target.value }))} />
          </FormRow>
          <FormRow label="Observaciones">
            <Textarea value={provDraft.observaciones} onChange={(e) => setProvDraft((d) => ({ ...d, observaciones: e.target.value }))} />
          </FormRow>
        </div>
      </Modal>

      {/* Modal: nuevo movimiento bolsa */}
      <Modal
        open={openBolsaForm}
        onClose={() => setOpenBolsaForm(false)}
        title="Movimiento de bolsa"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenBolsaForm(false)}>Cancelar</Button>
            <Button variant="create" onClick={submitBolsa} disabled={!bolsaDraft.descripcion.trim() || !bolsaDraft.importe_eur || createBolsa.isPending}>
              {createBolsa.isPending ? 'Creando…' : 'Crear'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormRow label="Fecha" required>
            <Input type="date" value={bolsaDraft.fecha} onChange={(e) => setBolsaDraft((d) => ({ ...d, fecha: e.target.value }))} />
          </FormRow>
          <FormRow label="Importe (EUR)" required hint="Positivo = entrada, negativo = salida.">
            <Input type="number" step="0.01" value={bolsaDraft.importe_eur} onChange={(e) => setBolsaDraft((d) => ({ ...d, importe_eur: e.target.value }))} />
          </FormRow>
          <FormRow label="Descripción" required>
            <Input value={bolsaDraft.descripcion} onChange={(e) => setBolsaDraft((d) => ({ ...d, descripcion: e.target.value }))} />
          </FormRow>
          <FormRow label="Observaciones">
            <Textarea value={bolsaDraft.observaciones} onChange={(e) => setBolsaDraft((d) => ({ ...d, observaciones: e.target.value }))} />
          </FormRow>
        </div>
      </Modal>

      {/* Modal: detalle de movimientos de un provisionamiento */}
      <Modal
        open={!!detalleProvId}
        onClose={() => setDetalleProvId(null)}
        title="Movimientos del provisionamiento"
        size="lg"
        footer={<Button variant="secondary" onClick={() => setDetalleProvId(null)}>Cerrar</Button>}
      >
        {detalleMov.isLoading ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Fecha</Th>
                  <Th>Nº doc</Th>
                  <Th>Producto</Th>
                  <Th align="right">Importe</Th>
                  <Th>Nota</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(detalleMov.data?.data ?? []).map((m) => (
                  <tr key={m.id}>
                    <Td>{fmt.date(m.fecha)}</Td>
                    <Td>{m.numero_documento ?? '—'}</Td>
                    <Td>{m.producto_nombre ? `${m.sku ?? ''} ${m.producto_nombre}` : '—'}</Td>
                    <Td align="right">{fmt.eur(m.importe_eur)}</Td>
                    <Td className="text-xs text-slate-500">{m.notas ?? '—'}</Td>
                  </tr>
                ))}
                {(detalleMov.data?.data ?? []).length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-xs italic text-slate-400">Sin movimientos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Sub: lista de provisionamientos
// ───────────────────────────────────────────────────────────────────────

function ProvList({ rows, loading, onClickDetail, onDelete }: {
  rows: Prov[];
  loading: boolean;
  onClickDetail: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (loading) return <Spinner />;
  if (rows.length === 0) return <p className="text-xs italic text-slate-400">Sin provisionamientos.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            <Th>Fecha</Th>
            <Th>Descripción</Th>
            <Th align="right">Importe</Th>
            <Th align="right">Provisionado</Th>
            <Th align="right">Pendiente</Th>
            <Th>Estado</Th>
            <Th align="right">Acciones</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((p) => (
            <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onClickDetail(p.id)}>
              <Td>{fmt.date(p.fecha)}</Td>
              <Td>{p.descripcion}</Td>
              <Td align="right">{fmt.eur(p.importe_eur)}</Td>
              <Td align="right">{fmt.eur(p.importe_provisionado_eur)}</Td>
              <Td align="right" className={Number(p.importe_pendiente_eur) > 0 ? 'text-amber-700' : 'text-slate-500'}>
                {fmt.eur(p.importe_pendiente_eur)}
              </Td>
              <Td>
                <Badge color={p.estado === 'ACTIVO' ? '#3b82f6' : '#94a3b8'}>{p.estado}</Badge>
              </Td>
              <Td align="right">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                >
                  Eliminar
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`px-3 py-2 text-${align} text-xs font-semibold uppercase tracking-wide text-slate-600`}>{children}</th>;
}

function Td({ children, align = 'left', className = '' }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return <td className={`px-3 py-2 text-${align} text-sm text-slate-700 ${className}`}>{children}</td>;
}
